import React, { CSSProperties } from 'react';
import clsx from 'clsx';
import TextField from '@material-ui/core/TextField';
import makeStyles from '@material-ui/core/styles/makeStyles';
import createStyles from '@material-ui/core/styles/createStyles';
import { OutlinedInputProps } from '@material-ui/core/OutlinedInput';

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {},
    inputRoot: {
      height: 48,
      padding: '0 12px',
      fontSize: 18,
      color: theme.palette.grey['700'],
      backgroundColor: theme.palette.common.white,
      '&:hover .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${theme.palette.grey.A200}`,
      },
      '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${theme.palette.grey.A200}`,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${theme.palette.secondary.main}`,
      },
      '& fieldset': {
        border: `1px solid ${theme.palette.grey.A200}`,
        borderRadius: 3,
      },
      [theme.breakpoints.down('xs')]: {
        height: 42,
        padding: '0 10px',
        fontSize: 16,
      },
    },
    input: {
      height: '100%',
      padding: 0,
      color: theme.palette.grey['700'],
      transition: theme.transitions.create(['border-color', 'box-shadow']),
      '&[type="date"]': {
        height: 'auto',
      },
      '&:focus': {
        borderColor: theme.palette.secondary.main,
      },
      [theme.breakpoints.down('xs')]: {
        padding: 0,
        fontSize: 16,
      },
    },
    inputSmallPalceholder: {
      '&::placeholder': {
        textOverflow: 'ellipsis !important',
        fontSize: 12,
      },
    },
    labelRoot: {
      color: theme.palette.grey['700'],
      opacity: 0.5,
      fontSize: 18,
      transform: 'translate(12px, 15px) scale(1)',
      whiteSpace: 'nowrap',
      '&.Mui-disabled': {
        color: theme.palette.grey['700'],
      },
      '&.Mui-focused': {
        color: theme.palette.secondary.main,
      },
      [theme.breakpoints.down('xs')]: {
        fontSize: 16,
        transform: 'translate(12px, 14px) scale(1)',
        '&.MuiInputLabel-shrink': {
          transform: 'translate(12px, -6px) scale(0.75)',
        },
      },
    },
    labelFocused: {
      opacity: 1,
    },
    labelShrink: {
      opacity: 1,
    },
    disabled: {
      backgroundColor: theme.palette.grey['100'],
    },
  }),
);

interface Props {
  onBlur?: OutlinedInputProps['onBlur'];
  onChange?: OutlinedInputProps['onChange'];
  onFocus?: OutlinedInputProps['onFocus'];
  type?: string;
  name?: string;
  value?: unknown;
  id?: string;
  variant?: any;
  fullWidth?: boolean;
  label?: React.ReactNode;
  InputLabelProps?: any;
  hiddenLabel?: boolean;
  placeholder?: string;
  InputProps?: any;
  inputProps?: any;
  helperText?: React.ReactNode;
  FormHelperTextProps?: any;
  required?: boolean;
  error?: boolean;
  disabled?: boolean;
  className?: string;
  margin?: 'normal' | 'none' | 'dense';
  style?: CSSProperties;
  inputRef?: React.Ref<any>;
  defaultValue?: any;
  smallPalceHolder?: boolean;
}

const Input: React.FC<Props> = ({
  onBlur,
  onChange,
  onFocus,
  type = 'text',
  name,
  value,
  id,
  variant = 'outlined',
  fullWidth = true,
  label,
  InputLabelProps,
  hiddenLabel,
  placeholder,
  InputProps,
  helperText,
  FormHelperTextProps,
  required = false,
  error = false,
  disabled = false,
  className,
  margin,
  style,
  inputRef,
  defaultValue,
  inputProps,
  smallPalceHolder,
}) => {
  const classes = useStyles();

  return (
    <TextField
      onBlur={onBlur}
      onChange={onChange}
      onFocus={onFocus}
      type={type}
      name={name}
      value={value}
      id={id}
      variant={variant}
      fullWidth={fullWidth}
      label={label}
      InputLabelProps={{
        classes: {
          root: classes.labelRoot,
          focused: classes.labelFocused,
          shrink: classes.labelShrink,
        },
        ...InputLabelProps,
      }}
      hiddenLabel={hiddenLabel}
      placeholder={placeholder}
      InputProps={{
        classes: {
          root: classes.inputRoot,
          input: clsx([classes.input, smallPalceHolder && classes.inputSmallPalceholder]),
          disabled: classes.disabled,
        },
        ...InputProps,
      }}
      inputProps={inputProps} // eslint-disable-line
      helperText={helperText}
      FormHelperTextProps={FormHelperTextProps}
      required={required}
      error={error}
      disabled={disabled}
      className={clsx(classes.root, className)}
      margin={margin}
      style={style}
      inputRef={inputRef}
      defaultValue={defaultValue}
    />
  );
};

export default Input;
